import { ApiGuardianEndpointDetails } from '@bn/app-shield';
import { ContextType } from '@bn/shared/entities/models';
import { DateTimeUtils } from '@bn/shared/utils';
import { TableRow } from '@bn/web-ui/table';

export const getMethodColor = (method: string | null): ContextType => {
  if (!method) return 'light';
  switch (method.toUpperCase()) {
    case 'GET':
      return 'info';
    case 'POST':
    case 'PATCH':
      return 'success';
    case 'PUT':
      return 'warning';
    case 'DELETE':
      return 'danger';
    default:
      return 'light';
  }
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';
  return DateTimeUtils.dayjs(dateString).format('h:mm A MMM DD, YYYY');
};

const getIndicatorContext = (endpoint: ApiGuardianEndpointDetails): ContextType => {
  if (!endpoint.isEnabled) return 'danger';
  if (!endpoint.validateRequestBodySchema && !endpoint.validateAuthorization) return 'danger';
  return 'success';
};

export const createTableRow = (
  endpoint: ApiGuardianEndpointDetails,
  onToggle?: (endpointId: number, isEnabled: boolean) => void,
  isLoading?: (endpointId: number) => boolean,
): TableRow => {
  const method = endpoint.requestMethod ?? 'GET';
  const schemaEnabled = endpoint.validateRequestBodySchema || endpoint.validateResponseBodySchema;
  const authEnabled = endpoint.validateAuthorization;
  const endpointId = endpoint.apiGuardianEndpointId;
  const isEndpointLoading = endpointId ? (isLoading?.(endpointId) ?? false) : false;

  return {
    itemId: endpoint.apiGuardianEndpointId,
    columns: [
      {
        id: 'toggle',
        valueType: 'switch',
        value: endpoint.isEnabled ? 1 : 0,
        rawValue: endpoint.isEnabled,
        action: 'toggle',
        width: '50px',
        isDisabled: isEndpointLoading,
        valueTypeOptions: {
          onChangeCallback: () => {
            if (endpoint.apiGuardianEndpointId) {
              onToggle?.(endpoint.apiGuardianEndpointId, !endpoint.isEnabled);
            }
          },
        },
      },

      {
        id: 'endpoint',
        value: method,
        valueType: 'badge',
        valueTypeOptions: {
          badgeType: 'tag',
          contextType: getMethodColor(method),
        },
        width: '90px',
      },

      {
        id: 'url',
        value: endpoint.requestPath ?? '',
        valueType: 'link',
        action: 'edit',
        hasEllipsis: true,
        valueTypeOptions: {
          tooltip: endpoint.requestPath ?? '',
          showHoverIcon: true,
        },
      },

      {
        id: 'schema',
        value: '',
        valueType: 'badge',
        valueTypeOptions: {
          badgeType: 'tag',
          contextType: schemaEnabled ? 'success' : 'danger',
          leftIcon: schemaEnabled ? 'check' : 'close',
        },
        width: '60px',
      },

      {
        id: 'auth',
        value: '',
        valueType: 'badge',
        valueTypeOptions: {
          badgeType: 'tag',
          contextType: authEnabled ? 'success' : 'danger',
          leftIcon: authEnabled ? 'check' : 'close',
        },
        width: '60px',
      },

      {
        id: 'rateLimiting',
        value: '',
        valueType: 'badge',
        valueTypeOptions: {
          badgeType: 'tag',
          contextType: endpoint.rateLimitingEnabled ? 'success' : 'danger',
          leftIcon: endpoint.rateLimitingEnabled ? 'check' : 'close',
        },
        width: '110px',
      },

      {
        id: 'update',
        value: formatDate(endpoint.updatedAt),
        valueType: 'string',
        width: '200px',
      },
    ],
  };
};
